import { Injectable } from '@angular/core';
import { CHECKOUT_PREVIEW_SIZE } from '@api-ui-app/src/app/checkout/services/checkout-item-previews-service';
import { OrderItem } from '@api-ui-app/src/app/orders/lib/order-item';
import { OrderProductPreviewType } from '@api-ui-app/src/app/orders/lib/order-product-preview-type.enum';
import { DesignStructure } from '@gelato-api-ui/core/designs/design-structure';
import { PrintAreaService } from '@gelato-api-ui/core/designs/services/print-area.service';
import { PreviewFileType } from '@gelato-api-ui/core/preflight/preview-file-type.enum';
import { Scene } from '@gelato-api-ui/core/preflight/scene';
import { DesignPreviewFileTypeService } from '@gelato-api-ui/core/preflight/services/design-preview-file-type.service';
import { DesignProductPreviewUrlParamsProvider } from '@gelato-api-ui/core/preflight/services/design-product-preview-url-params.provider';
import { PreflightApiService } from '@gelato-api-ui/core/preflight/services/preflight-api.service';
import { PreflightProductPreviewRequestProvider } from '@gelato-api-ui/core/preflight/services/preflight-product-preview-request.provider';
import { DesignProductPreviewUrlParams } from '@gelato-api-ui/core/preflight/types/design-product-preview-url-params';
import { BasePrintAreaCode } from '@gelato-api-ui/core/print-areas/constants/print-area-code';
import { PrintAreaName } from '@gelato-api-ui/core/print-areas/constants/print-area-name';
import { ProductTypeUid } from '@gelato-api-ui/core/product-catalogue/product-type-uid.enum';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class CheckoutProductPreviewUrlProvider {
  constructor(
    private readonly designPreviewFileTypeService: DesignPreviewFileTypeService,
    private readonly preflightApiService: PreflightApiService,
    private readonly designProductPreviewUrlParamsProvider: DesignProductPreviewUrlParamsProvider,
    private readonly preflightProductPreviewRequestProvider: PreflightProductPreviewRequestProvider,
    private readonly printAreaService: PrintAreaService,
  ) {}

  get(scene: string, item: OrderItem, designStructure: string): Observable<string> {
    const { productUid } = item;
    const size = CHECKOUT_PREVIEW_SIZE;

    return this.getProductPreviewUrlParams(
      productUid,
      designStructure ? JSON.parse(designStructure) : null,
      scene,
      this.designPreviewFileTypeService.getByProductTypeUid(item.productTypeUid as ProductTypeUid),
      size,
      size,
      false,
      item.previews.find(productPreview => productPreview.type === OrderProductPreviewType.FLAT)?.url,
    ).pipe(
      take(1),
      map(designProductPreviewUrlParams =>
        this.preflightProductPreviewRequestProvider.get(
          productUid,
          designProductPreviewUrlParams,
          size,
          size,
          scene,
          true,
          PreviewFileType.JPEG,
        ),
      ),
      switchMap(request => (request ? this.preflightApiService.getProductPreviewUrl(request) : of(null))),
    );
  }

  private getProductPreviewUrlParams(
    productUid: string,
    designStructure: DesignStructure,
    previewScene: string,
    designPreviewFileType: PreviewFileType,
    width: number,
    height: number,
    removeSamples: boolean,
    flatPreview: string,
  ): Observable<DesignProductPreviewUrlParams> {
    if (designStructure) {
      return this.designProductPreviewUrlParamsProvider.get(
        productUid,
        designStructure,
        designPreviewFileType,
        width,
        height,
        removeSamples,
      );
    } else {
      if (this.printAreaService.isApparelScene(previewScene as Scene)) {
        const expectedSpreadName = this.printAreaService.getExpectedSpreadNameByScene(previewScene as Scene);
        const sides = productUid.includes(BasePrintAreaCode.Back) ? [PrintAreaName.Back] : [PrintAreaName.Front];

        if (flatPreview && !sides.includes(expectedSpreadName)) {
          return of(null);
        }
      }

      return of({ imageUrl: flatPreview });
    }
  }
}
